import { createTheme } from '@mui/material/styles'

export type ColorMode = 'light' | 'dark' | 'high-contrast'
export type FontPreference = 'default' | 'atkinson' | 'opendyslexic'

const fontFamilies: Record<FontPreference, string> = {
  default: '"Roboto", "Helvetica", "Arial", sans-serif',
  atkinson: '"Atkinson Hyperlegible", "Roboto", "Helvetica", "Arial", sans-serif',
  opendyslexic: '"OpenDyslexic", "Roboto", "Helvetica", "Arial", sans-serif',
}

type FontTypographyOverride = {
  body1?: { lineHeight?: number; letterSpacing?: string }
  body2?: { lineHeight?: number; letterSpacing?: string }
  fontWeightLight?: number
}

// Per-font typography adjustments beyond just fontFamily.
// Dyslexia-friendly fonts benefit from increased line-height;
// OpenDyslexic has no weight below 400.
const fontTypographyOverrides: Record<FontPreference, FontTypographyOverride> = {
  default: {},
  atkinson: {
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.65 },
  },
  opendyslexic: {
    body1: { lineHeight: 1.8, letterSpacing: '-0.03em' },
    body2: { lineHeight: 1.8, letterSpacing: '-0.03em' },
    fontWeightLight: 400,
  },
}

export function createAppTheme(mode: ColorMode = 'light', font: FontPreference = 'default') {
  const isLight = mode === 'light'
  const isHighContrast = mode === 'high-contrast'

  // MUI's palette.mode only accepts 'light' | 'dark'.
  // High-contrast uses 'dark' as its MUI base so that all MUI components
  // render with dark-mode defaults; the palette overrides below push every
  // colour to WCAG AAA ratios.
  const muiPaletteMode = isLight ? ('light' as const) : ('dark' as const)

  const h1Color = isHighContrast ? '#c8a0ff' : isLight ? '#3b1a59' : '#c9a8e8'

  // In high-contrast mode the ultra-light heading weights (100–200) are hard
  // to read even with correct colour contrast. Step them up to legible weights.
  const headingWeights = isHighContrast
    ? { h1: 400, h2: 400, h3: 600, h4: 600 }
    : { h1: 100, h2: 100, h3: 200, h4: 100 }

  // OpenDyslexic glyphs are naturally wide; scale the root font-size down 25%
  // so nav items and compact UI don't overflow into ellipsis. Using htmlFontSize
  // keeps MUI's pxToRem helper in sync so spacing stays correct.
  const htmlFontSize = font === 'opendyslexic' ? 12 : 16 // 16 * 0.75 = 12

  // In dark/HC modes the default outlined button border is rendered at 50% opacity
  // of the palette colour, which is too faint on dark content backgrounds.
  // Boost to full-opacity secondary.light so outlined buttons read clearly.
  const outlinedBtnColor = isHighContrast ? '#99d6ff' : '#80b3ff' // dark: 7.25:1 contrast; HC: 9.92:1
  const outlinedBtnHoverBg = isHighContrast ? 'rgba(153, 214, 255, 0.08)' : 'rgba(60, 118, 216, 0.08)'

  return createTheme({
    typography: {
      fontFamily: fontFamilies[font],
      htmlFontSize,
      h1: { fontSize: '2rem', fontWeight: headingWeights.h1, color: h1Color },
      h2: { fontSize: '1.75rem', fontWeight: headingWeights.h2 },
      h3: { fontSize: '1.5rem', fontWeight: headingWeights.h3 },
      h4: { fontSize: '1.25rem', fontWeight: headingWeights.h4 },
      ...fontTypographyOverrides[font],
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Reducing html font-size scales all rem-based MUI typography globally.
          // Only active for OpenDyslexic — other fonts leave it at the browser default.
          html: { fontSize: font === 'opendyslexic' ? '75%' : undefined },
        },
      },
      MuiButton: {
        styleOverrides: {
          // Outlined primary/secondary buttons are hard to read in dark/HC modes because
          // MUI renders the border at 50% opacity of the palette colour. Target the MUI
          // colour classes directly inside the outlined override (compound class names are
          // not generated in MUI v7, so outlinedPrimary/outlinedSecondary keys don't work).
          outlined: !isLight
            ? {
                '&.MuiButton-colorPrimary, &.MuiButton-colorSecondary': {
                  color: outlinedBtnColor,
                  borderColor: outlinedBtnColor,
                  '&:hover': { borderColor: outlinedBtnColor, backgroundColor: outlinedBtnHoverBg },
                },
              }
            : {},
          text: !isLight
            ? {
                '&.MuiButton-colorPrimary, &.MuiButton-colorSecondary': {
                  color: outlinedBtnColor,
                  '&:hover': { backgroundColor: outlinedBtnHoverBg },
                },
              }
            : {},
        },
      },
      MuiAppBar: { styleOverrides: { root: { height: 64 } } },
      MuiListItemButton:
        font === 'opendyslexic' ? { styleOverrides: { root: { paddingLeft: 8, paddingRight: 8 } } } : {},
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '.MuiInputBase-readOnly ~ div > button > svg': {
              display: 'none',
            },
            '.MuiInputBase-readOnly > .MuiChip-root:focus': {
              borderColor: '#51247A !important',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: 'standard' },
        styleOverrides: {
          root: {
            '& .MuiInputBase-readOnly::before, .MuiInputBase-readOnly::after': {
              borderBottomColor: isLight ? 'rgba(0,0,0,0.1) !important' : 'rgba(255,255,255,0.15) !important',
              borderBottomWidth: '1px !important',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: !isLight
            ? {
                '&.Mui-focused': {
                  color: isHighContrast ? '#d9b3ff' : '#c9a8e8',
                },
              }
            : {},
        },
      },
      MuiInput: {
        styleOverrides: {
          root: !isLight
            ? {
                '&.Mui-focused::after': {
                  borderBottomColor: isHighContrast ? '#d9b3ff' : '#c9a8e8',
                },
              }
            : {},
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          root: { cursor: 'pointer', '&.Mui-disabled': { cursor: 'pointer' } },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standard: !isLight
            ? {
                '&.MuiAlert-colorWarning': {
                  backgroundColor: 'rgba(255, 193, 7, 0.12)',
                  color: 'rgb(255, 218, 117)',
                  '& .MuiAlert-icon': { color: 'rgb(255, 218, 117)' },
                },
              }
            : {},
          filled: {
            '&.MuiAlert-colorWarning': {
              color: '#000',
              ...(!isLight && { backgroundColor: 'rgb(255, 218, 117)' }),
              a: { color: '#000', '&:hover': { textDecoration: 'underline' } },
            },
            '&.MuiAlert-colorError': {
              a: { color: '#FFF', '&:hover': { textDecoration: 'underline' } },
            },
            '&.MuiAlert-colorInfo': {
              backgroundColor: '#1f5abf',
              a: { color: '#FFF', '&:hover': { textDecoration: 'underline' } },
            },
            '&.MuiAlert-colorSuccess': {
              a: { color: '#FFF', '&:hover': { textDecoration: 'underline' } },
            },
          },
          message: { flex: 1 },
        },
      },
      MuiTypography: {
        variants: !isLight
          ? [
              {
                props: { color: 'primary' },
                style: { '&&': { color: isHighContrast ? '#d9b3ff' : '#c9a8e8' } },
              },
            ]
          : [],
        defaultProps: {
          variantMapping: {
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            subtitle1: 'span',
            subtitle2: 'span',
            body1: 'p',
            body2: 'p',
            inherit: 'p',
          },
        },
      },
    },
    palette: {
      mode: muiPaletteMode,
      ...(isHighContrast
        ? {
            // High-contrast palette — all text/UI colours exceed WCAG AAA (7:1) on black.
            background: { default: '#000000', paper: '#0d0d0d' },
            primary: { light: '#d9b3ff', main: '#c8a0ff', dark: '#a060e0' },
            secondary: { light: '#99d6ff', main: '#80c0ff', dark: '#5599cc' },
            error: { light: '#ffaaaa', main: '#ff8080', dark: '#cc5555' },
            warning: { light: '#ffd280', main: '#ffbb40', dark: '#cc8800' },
            success: { light: '#aaffaa', main: '#80ff80', dark: '#44cc44' },
            info: { light: '#99eeff', main: '#60ddff', dark: '#2299cc' },
            divider: '#505050',
          }
        : isLight
          ? {
              background: { default: '#fafafa' },
              primary: { light: '#962A8B', main: '#51247A', dark: '#3b1a59' },
              secondary: { light: '#3c76d8', main: '#2464d0', dark: '#1f56b1' },
              warning: {
                light: 'rgb(255, 218, 117)',
                main: 'rgb(251, 184, 0)',
                dark: 'rgb(126, 92, 0)',
              },
              success: { light: '#00a700', main: '#238029', dark: '#005000' },
              error: { light: '#ff0000', main: '#951126', dark: '#790000' },
              info: {
                light: 'rgb(138, 214, 255)',
                main: 'rgb(2, 136, 209)',
                dark: 'rgb(0, 76, 117)',
              },
            }
          : {
              // Dark palette
              background: { default: '#242424', paper: '#1e1e1e' },
              primary: { light: '#c9a8e8', main: '#a476cc', dark: '#7a4ea5' },
              secondary: { light: '#3c76d8', main: '#2464d0', dark: '#1f56b1' },
              warning: {
                light: 'rgb(255, 218, 117)',
                main: 'rgb(251, 184, 0)',
                dark: 'rgb(126, 92, 0)',
              },
              success: { light: '#00a700', main: '#238029', dark: '#005000' },
              error: { light: '#ff6b6b', main: '#ff6b6b', dark: '#cc4444' },
              info: {
                light: 'rgb(138, 214, 255)',
                main: 'rgb(2, 136, 209)',
                dark: 'rgb(0, 76, 117)',
              },
            }),
    },
  })
}

// Backward-compatible static export — used by any consumers that haven't
// migrated to ThemeContextProvider yet.
export const muiTheme = createAppTheme('light', 'default')
